import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useDispatch } from 'react-redux';
import Text from '../../../shared/components/Text/Text';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import { undoCalendarAction } from '../ducks/calendarActions';

const ModalContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
`;

const IconCircle = styled('div')`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #F5F5F5;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalText = styled('div')`
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
`;

const WarningText = styled('div')`
  padding: 12px;
  background: #FFF3CD;
  border: 1px solid #FFE69C;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  text-align: left;
  width: 100%;
  justify-content: center;
  margin-bottom: 20px;
`;

const ButtonsContainer = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConfirmButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #4A90E2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #3A7FC1;
  }
`;

const CancelButton = styled('button')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  font-weight: 700;
  width: 100%;
  height: 48px;
  background: #3C3C3C;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #2C2C2C;
  }
`;

const CloseButton = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  cursor: pointer;
`;

const ConfirmUndoModal = ({ closeModal, modalData }) => {
  const dispatch = useDispatch();
  const { entry, cascadedCount, accountCode } = modalData || {};

  const handleCancel = () => {
    closeModal();
  };

  const handleConfirm = () => {
    closeModal();
    dispatch(undoCalendarAction(accountCode, { id: entry.id, expiresAt: entry.expiresAt }));
  };

  return (
    <ModalContainer>
      <CloseButton onClick={handleCancel}>
        <IcomoonReact
          iconSet={iconSet}
          size={15}
          icon='remove'
          color='grey'
        />
      </CloseButton>
      <IconCircle>
        <IcomoonReact
          iconSet={iconSet}
          size={32}
          icon='warning'
          color='#DB3030'
        />
      </IconCircle>

      <ModalText>
        <Text fontSize='20px' fontWeight={600}>
          Undo this change?
        </Text>
      </ModalText>

      <WarningText>
        <IcomoonReact
          iconSet={iconSet}
          size={16}
          icon='warning'
          color='#856404'
          style={{ marginRight: '8px', marginTop: '2px', flexShrink: 0 }}
        />
        <Text fontSize='14px' color='#856404'>
          {`This will also undo ${cascadedCount} more recent change${cascadedCount === 1 ? '' : 's'}.`}
        </Text>
      </WarningText>

      <ButtonsContainer>
        <ConfirmButton onClick={handleConfirm}>
          UNDO
        </ConfirmButton>
        <CancelButton onClick={handleCancel}>
          CANCEL
        </CancelButton>
      </ButtonsContainer>
    </ModalContainer>
  );
};

ConfirmUndoModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    entry: PropTypes.shape({
      id: PropTypes.number,
      expiresAt: PropTypes.string,
    }),
    cascadedCount: PropTypes.number,
    accountCode: PropTypes.string,
  }).isRequired,
};

export default ConfirmUndoModal;
